import React, { useEffect, useState } from 'react';
import * as Yup from "yup";
import { Col, Row, Label, Button, Form, Card, CardHeader, CardBody, CardFooter, Input } from "reactstrap";
import { useQuery } from 'react-query';
import { queryKeyes } from 'shared/queryKeys';
import { FormikErrors, FormikTouched, useFormik } from 'formik';
import ReportDetailsHeader from 'Components/ReportDetailsHeader';
import FormValuesDebug from 'utils/debugTools/FormValuesDebug';
import apiGlobal from 'global/api.global';
import { Get } from 'VesselMaster/vesselMaster.hooks';
import { commonValidationMessages } from 'Components/ValidationErrorMessages';
import { queryClient } from 'react-query/queryClient';
import { dateTimeFormat, compareObjectIgnoreType, handleServerResponse } from 'GenericForms/Helper';
import env from 'environment_system/env_system';
import { useDocumentTitle } from 'Components/useDocument.hooks';
import Select from 'react-select';
import { customStyle } from 'shared/CommonCSS';
import ErrorComponent from 'Components/ErrorComponent';
import DeletePopOver from 'Components/DeletePopOver';
import { STSOperationObjectType, STSOperationType } from './DeckOfficerReportingTypes';
import ErrorTooltip from 'Components/ErrorTooltip';
import { successToast } from 'Components/Toasts';
import { RootState } from 'index';
import { useSelector } from 'react-redux';
import FormLoading from 'Components/FormLoading';
import { useEditReport } from 'VesselReportingInfo/EditCompletedReports/EditReportProvider';

interface STSOperationComponentType {
    toggleTab?: any,
    setErrorMessage?: any
    editReportBool?: boolean;
}

const STSOperationComponent = ({
    toggleTab,
    setErrorMessage,
    editReportBool = false
}: STSOperationComponentType) => {
    const { previousDeckTabIndex: activeTab, discardwarning } = useEditReport();
    /** State varibles */
    useDocumentTitle('STS Operation Report - ecoSAIL');
    const { VesselID, ReportID, CurrentReport, PreviousReport } = useSelector((state: RootState) => state.Reporting);
    const currentVesselReport = CurrentReport?.data?.vessel_reporting_information ?? null;
    const previousVesselReport = PreviousReport?.data?.vessel_reporting_information ?? null;
    const [deletePopoverBool, setDeletePopoverBool] = useState(false);
    const [deleteIndex, setDeleteIndex] = useState<number>(null);
    const [deleteOperation, setDeleteOperation] = useState<any>(null);
    const [refreshKey, setRefreshKey] = useState<number>(0);
    const [filteredOperations, setFilteredOperations] = useState<Array<number>>([]);
    /** State variables end */

    /** Queries */
    /** STS operation master */
    const { data: STSMaster, isLoading: STSMasterLoading, isError: STSMasterError }:
        { data: any, isLoading: any, isError: any } = useQuery(
            [queryKeyes.masters.STSOperationsMaster.key],
            async () => {
                return await Get(queryKeyes.masters.STSOperationsMaster.url());
            },
            { staleTime: Infinity }
        )
    /** STS ongoing reporting object used for edit */
    const { data: STSOngoingReportingObject, isLoading: STSOngoingReportingObjectLoading, isError: STSOngoingReportingObjectError }:
        { data: any, isLoading: any, isError: any } = useQuery(
            [queryKeyes.vessel.STSOngoingReportingObject.key, VesselID, ReportID],
            async () => {
                return await Get(queryKeyes.vessel.STSOngoingReportingObject.url(VesselID, ReportID), VesselID, ReportID);
            },
            { staleTime: Infinity }
        )
    /** Queries end */

    /** Assign values to initial object of STS Loading */
    const getInitialValues = () => {
        if (STSOngoingReportingObject?.length > 0 && STSOngoingReportingObject[0]?.id > 0) {
            return STSOngoingReportingObject;
        } else {
            return [{
                loading: false,
                sts_start_time: null,
                sts_end_time: null,
                ongoing_status: false,
                vessel: VesselID,
                sts_start_report: ReportID,
                sts_end_report: null,
                user_approved: false,
                class_approved: false,
                sts_operation: null,
                sts_operation_name: null
            }]
        }
    }

    /** UseEffect */
    useEffect(() => {
        STSOperationFormik.initialValues.sts = getInitialValues();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [STSOngoingReportingObject])
    /** UseEffect end */

    /** STS Operation formik object */
    const STSOperationFormik = useFormik<STSOperationType>({
        enableReinitialize: true,
        initialValues: {
            sts: getInitialValues(),
        },
        validationSchema: env?.form_validation &&
            Yup.object().shape({
                sts: Yup.array(
                    Yup.object({
                        sts_operation: Yup.number().required(commonValidationMessages.required),
                    }).test('customValidations', "", function (value: any) {
                        value.sts_start_time = dateTimeFormat(value?.sts_start_time);
                        const index: number = STSOperationFormik?.values?.sts?.findIndex(
                            (item: any) => item.sts_operation === value?.sts_operation && dateTimeFormat(item?.sts_start_time) === value?.sts_start_time
                        );
                        if (index >= 0) {
                            if (value && dateTimeFormat(value?.sts_start_time) === null) {
                                return new Yup.ValidationError(commonValidationMessages.required, undefined, `sts.${index}.sts_start_time`);
                            }
                            if (STSOperationFormik?.values?.sts?.filter(
                                (item: any) => item.sts_operation === value?.sts_operation &&
                                    !compareObjectIgnoreType({
                                        ...item,
                                        sts_start_time: dateTimeFormat(item?.sts_start_time)
                                    }, value) && dateTimeFormat(item?.sts_start_time) === value?.sts_start_time)
                                ?.length > 0) {
                                throw new Yup.ValidationError("STS operation with same start time already exists", undefined, `sts.${index}.sts_start_time`);
                            }
                            if (value && value?.sts_start_report === ReportID) {
                                if (dateTimeFormat(value?.sts_start_time) < dateTimeFormat(previousVesselReport?.local_time)) {
                                    return new Yup.ValidationError
                                        (
                                            `${commonValidationMessages.minDateLastRecord} (${dateTimeFormat(previousVesselReport?.local_time, false, false, true)})`,
                                            undefined,
                                            `sts.${index}.sts_start_time`
                                        );
                                } else if (dateTimeFormat(value?.sts_start_time) > dateTimeFormat(currentVesselReport?.local_time)) {
                                    return new Yup.ValidationError(commonValidationMessages.maxDateCurrentRecord, undefined, `sts.${index}.sts_start_time`);
                                }
                            }
                            if (dateTimeFormat(value?.sts_end_time) < dateTimeFormat(value?.sts_start_time)) {
                                throw new Yup.ValidationError(commonValidationMessages.minStartDate, undefined, `sts.${index}.sts_end_time`);
                            } else if (dateTimeFormat(value?.sts_end_time) < dateTimeFormat(previousVesselReport?.local_time)) {
                                throw new Yup.ValidationError(`${commonValidationMessages.minDateLastRecord} (${dateTimeFormat(previousVesselReport?.local_time, false, false, true)})`, undefined, `sts.${index}.sts_end_time`);
                            } else if (dateTimeFormat(value?.sts_end_time) > dateTimeFormat(currentVesselReport?.local_time)) {
                                return new Yup.ValidationError(commonValidationMessages.maxDateCurrentRecord, undefined, `sts.${index}.sts_end_time`);
                            }
                            if (value?.sts_start_time && value?.sts_start_report === ReportID) {
                                STSOperationFormik?.values?.sts?.filter((item: any) => item.sts_operation === value?.sts_operation &&
                                    !compareObjectIgnoreType({
                                        ...item,
                                        sts_start_time: dateTimeFormat(item?.sts_start_time)
                                    }, value))
                                    ?.forEach((operation: STSOperationObjectType) => {
                                        if (operation?.sts_start_time && dateTimeFormat(operation?.sts_start_time) <= dateTimeFormat(value?.sts_start_time) &&
                                            dateTimeFormat(value?.sts_start_time) <= dateTimeFormat(operation?.sts_end_time)) {
                                            throw new Yup.ValidationError(commonValidationMessages.STSOpConflict, undefined, `sts.${index}.sts_start_time`);
                                        }
                                    })
                            }
                            return true;
                        }
                        return true;
                    }),
                )
            }),
        onSubmit: async (values: any, actions: any) => {
            if (env?.form_validation === false) {
                setErrorMessage(null)
                toggleTab(activeTab + 1);
                return;
            }
            actions.setSubmitting(true);
            const responseArray: any[] = [];
            /** STS Loading submit */
            if (STSOngoingReportingObject?.length > 0 && STSOngoingReportingObject[0]?.id > 0) {
                values?.sts?.forEach((operation: STSOperationObjectType) => {
                    if (operation?.id && operation?.id > 0) {
                        responseArray.push(apiGlobal.put(`/sts_operation_detail/${operation?.id}/`, operation));
                    } else {
                        responseArray.push(apiGlobal.post(`/sts_operation_detail/`, operation));
                    }
                })
            } else {
                responseArray.push(apiGlobal.post(`/sts_operation_detail/`, values.sts))
            }
            await handleServerResponse(responseArray).then(async res => {
                if (res === true) {
                    if (env?.form_validation === true) {
                        setErrorMessage(null)
                        toggleTab(activeTab + 1);
                        await queryClient.invalidateQueries(queryKeyes.vessel.STSOngoingReportingObject.key);
                        setRefreshKey(refreshKey + 1);
                    }
                } else {
                    setErrorMessage(res)
                }
                actions.setSubmitting(false);
            })
        }
    });

    useEffect(() => {
        let array = [...filteredOperations as number[]];
        STSOperationFormik?.values?.sts?.filter((operation: STSOperationObjectType) => {
            if ((operation?.sts_start_time !== null || operation?.sts_start_time !== undefined || operation?.sts_start_time !== "") &&
                (operation.sts_end_time === null || operation.sts_end_time === undefined || operation.sts_end_time === "")) {
                if (!array?.includes(operation?.sts_operation as number)) {
                    array.push(operation.sts_operation as number);
                }
            } else {
                if (array?.includes(operation?.sts_operation as number)) {
                    array.splice(filteredOperations?.indexOf(operation?.sts_operation as number), 1);
                }
            }
            return "";
        })
        array.forEach((item: any, index: number) => {
            if (STSOperationFormik?.values?.sts?.some((sts: any) => sts.sts_operation === item) === false) {
                array.splice(index, 1);
            }
        })
        setFilteredOperations(array);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [STSOperationFormik?.values?.sts, refreshKey])

    const stsErrors = STSOperationFormik.errors?.sts as FormikErrors<any>[];
    const stsTouched = STSOperationFormik.touched?.sts as FormikTouched<any>[];
    return (
        <React.Fragment key={refreshKey}>
            <Card className='p-0 mb-0 border-0'>
                <CardHeader className='p-2 border-bottom-0'>
                    <div className="text-center">
                        <Row>
                            <Col>
                                <h4 className="page_title pos-start mb-0">STS Operation</h4>
                                <p className="card-title-desc pos-start">All readings since last report</p>
                            </Col>
                            <Col>
                                <ReportDetailsHeader />
                            </Col>
                        </Row>
                    </div>
                </CardHeader>
                {(STSOngoingReportingObjectLoading || STSMasterLoading) &&
                    <FormLoading />}
                {STSOngoingReportingObjectError && getInitialValues()}
                {STSMasterError && <ErrorComponent message="Error loading required data!" />}
                {!(STSOngoingReportingObjectLoading || STSMasterLoading) && !STSMasterError &&
                    <Form autoComplete="off" onSubmit={STSOperationFormik?.handleSubmit} noValidate key={refreshKey}>
                        <CardBody className="px-2 py-0 mt-2">
                            {STSOperationFormik && STSOperationFormik?.values?.sts?.map((operation: STSOperationObjectType, index: any) => {
                                return (
                                    <React.Fragment key={`${refreshKey}_${index}`}>
                                        <Row className='mb-2'>
                                            <Col lg={4}>
                                                <Label className='mb-0 asteric' for={`sts.${index}.sts_operation`}>STS operation</Label>
                                                <Select
                                                    name={`sts.${index}.sts_operation`}
                                                    inputId={`sts_${index}_sts_operation`}
                                                    options={STSMaster?.filter((item: any) => {
                                                        return !filteredOperations?.includes(item?.id as number);
                                                    })}
                                                    getOptionLabel={(e: any) => e.sts_operation_name}
                                                    getOptionValue={(e: any) => e.id}
                                                    onBlur={(e) => {
                                                        STSOperationFormik.handleBlur(e);
                                                        STSOperationFormik.setFieldTouched(`sts.${index}.sts_operation`, true);

                                                    }}
                                                    onChange={(selectedOption: any) => {
                                                        STSOperationFormik.setFieldValue(`sts.${index}.sts_operation`, selectedOption.id);
                                                        STSOperationFormik.setFieldValue(`sts.${index}.sts_operation_name`, selectedOption.sts_operation_name);
                                                        setRefreshKey(refreshKey + 1);
                                                    }}
                                                    defaultValue={{
                                                        id: operation?.sts_operation,
                                                        sts_operation_name: operation?.sts_operation_name
                                                    }}
                                                    menuPortalTarget={document.body}
                                                    styles={customStyle}
                                                />
                                                {stsErrors && stsErrors[index] && stsErrors[index]?.sts_operation &&
                                                    stsTouched && stsTouched[index]?.sts_operation &&
                                                    env?.form_validation === true && document.getElementById(`sts_${index}_sts_operation`) !== null && !discardwarning &&
                                                    <ErrorTooltip
                                                        target={`sts_${index}_sts_operation`}
                                                        message={stsErrors[index]?.sts_operation}
                                                        open={(stsErrors && stsErrors[index]?.sts_operation) ? true : false}
                                                    />
                                                }
                                            </Col>
                                            <Col lg={2}>
                                                <Label className={`mb-0 asteric
                                                    ${(operation && (operation?.sts_start_report === ReportID || operation?.sts_start_report === null)) ? "mb-0" : "mb-2"}`}
                                                    for={`sts.${index}.sts_start_time`}>Start time (Local)</Label><br />
                                                {(operation && (operation?.sts_start_report === ReportID || operation?.sts_start_report === null)) ?
                                                    <Input
                                                        onFocus={(e) => e.target.select()}
                                                        name={`sts.${index}.sts_start_time`}
                                                        type="datetime-local"
                                                        min={dateTimeFormat(previousVesselReport?.local_time)}
                                                        max={dateTimeFormat(currentVesselReport?.local_time)}
                                                        className="datetimepicker mt-0 text-uppercase"
                                                        id={`sts_${index}_sts_start_time`}
                                                        onBlur={(e: any) => STSOperationFormik?.handleBlur(e)}
                                                        onChange={(e: any) => {
                                                            if (e.target.value) {
                                                                STSOperationFormik?.handleChange(e);
                                                            } else {
                                                                STSOperationFormik?.setFieldValue(`sts.${index}.sts_start_time`, null);
                                                                STSOperationFormik?.setFieldValue(`sts.${index}.sts_start_report`, null);
                                                            }
                                                            if (e.target.value !== null && e.target.value !== undefined && e.target.value !== "") {
                                                                STSOperationFormik?.setFieldValue(`sts.${index}.sts_start_report`, ReportID);
                                                                STSOperationFormik?.setFieldValue(`sts.${index}.ongoing_status`, true);
                                                            }
                                                        }}
                                                        defaultValue={dateTimeFormat(STSOperationFormik?.values?.sts[index]?.sts_start_time as string, false, true)}
                                                    /> :
                                                    <strong
                                                        className='mb-0'
                                                        id={`sts_${index}_sts_start_time`}
                                                    >
                                                        {dateTimeFormat(operation?.sts_start_time as string, false, false, true)}
                                                    </strong>
                                                }
                                                {stsErrors && stsErrors[index] && stsErrors[index]?.sts_start_time &&
                                                    stsTouched && stsTouched[index]?.sts_start_time &&
                                                    env?.form_validation === true && document.getElementById(`sts_${index}_sts_start_time`) !== null && !discardwarning &&
                                                    <ErrorTooltip
                                                        target={`sts_${index}_sts_start_time`}
                                                        message={stsErrors[index]?.sts_start_time}
                                                        open={(stsErrors && stsErrors[index]?.sts_start_time) ? true : false}
                                                        key={stsErrors[index]?.sts_start_time as string}
                                                    />
                                                }
                                            </Col>
                                            <Col lg={2} className='ps-4'>
                                                <Label className={`mb-0
                                                    ${(operation && (operation?.sts_end_report === ReportID || operation?.sts_end_report === null)) ? "mb-0" : "mb-2"}`}
                                                    for={`sts.${index}.sts_end_time`}>End time (Local)</Label><br />
                                                {(operation && (operation?.sts_end_report === ReportID || operation?.sts_end_report === null)) ?
                                                    <Input
                                                        onFocus={(e) => e.target.select()}
                                                        name={`sts.${index}.sts_end_time`}
                                                        type="datetime-local"
                                                        min={dateTimeFormat(STSOperationFormik?.values?.sts[index]?.sts_start_time as string)}
                                                        max={dateTimeFormat(currentVesselReport?.local_time)}
                                                        className="form-control datetimepicker mt-0 text-uppercase"
                                                        id={`sts_${index}_sts_end_time`}
                                                        onBlur={(e: any) => STSOperationFormik?.handleBlur(e)}
                                                        onChange={(e: any) => {
                                                            STSOperationFormik?.handleChange(e);
                                                            if (e.target.value !== null && e.target.value !== undefined && e.target.value !== "") {
                                                                STSOperationFormik?.setFieldValue(`sts.${index}.sts_end_report`, ReportID);
                                                                STSOperationFormik?.setFieldValue(`sts.${index}.ongoing_status`, false);
                                                            } else {
                                                                STSOperationFormik?.setFieldValue(`sts.${index}.sts_end_time`, null);
                                                                STSOperationFormik?.setFieldValue(`sts.${index}.sts_end_report`, null);
                                                                if (operation.sts_start_report) {
                                                                    STSOperationFormik?.setFieldValue(`sts.${index}.ongoing_status`, true);
                                                                }
                                                            }
                                                        }}
                                                        defaultValue={dateTimeFormat(STSOperationFormik?.values?.sts[index]?.sts_end_time as string, false, true)}
                                                        disabled={
                                                            STSOperationFormik?.values?.sts[index]?.sts_start_time === null ||
                                                            STSOperationFormik?.values?.sts[index]?.sts_start_time === undefined ||
                                                            STSOperationFormik?.values?.sts[index]?.sts_start_time === ""
                                                        }
                                                    /> :
                                                    <strong
                                                        className='mb-0'
                                                        id={`sts_${index}_sts_end_time`}
                                                    >
                                                        {dateTimeFormat(operation?.sts_end_time as string, false, false, true)}
                                                    </strong>
                                                }
                                                {stsErrors && stsErrors[index] && stsErrors[index]?.sts_end_time &&
                                                    stsTouched && stsTouched[index]?.sts_end_time &&
                                                    env?.form_validation === true && document.getElementById(`sts_${index}_sts_end_time`) !== null && !discardwarning &&
                                                    <ErrorTooltip
                                                        target={`sts_${index}_sts_end_time`}
                                                        message={stsErrors[index]?.sts_end_time}
                                                        open={(stsErrors && stsErrors[index]?.sts_end_time) ? true : false}
                                                    />
                                                }
                                            </Col>
                                            <Col lg={{ size: 1, offset: 3 }} className='d-flex align-items-center'>
                                                {STSOperationFormik?.values?.sts?.length > 1 &&
                                                    operation?.sts_start_report === ReportID &&
                                                    operation?.sts_end_time === null &&
                                                    <button type="button" className="btn justify_right">
                                                        <i className='dripicons-trash icon_s18' id={`delete_sts_operation_${index}`}
                                                            onClick={() => {
                                                                setDeletePopoverBool(true);
                                                                setDeleteIndex(index);
                                                                setDeleteOperation(operation);
                                                                STSOperationFormik?.setErrors({});
                                                            }}
                                                        />
                                                    </button>
                                                }
                                            </Col>
                                        </Row>
                                        <Col>
                                            {index === STSOperationFormik?.values?.sts?.length - 1 &&
                                                STSOperationFormik?.values?.sts?.length < 10 &&
                                                STSMaster?.filter((item: any) => {
                                                    return !filteredOperations?.includes(item?.id as number);
                                                })?.length > 0 &&
                                                STSOperationFormik?.values?.sts?.[index]?.sts_operation !== null && // <-- prevent showing button
                                                <Label
                                                    id="add-sts-operation"
                                                    className="link_color_blue mt-2 mb-0"
                                                    onClick={() => {
                                                        STSOperationFormik?.values?.sts?.push({
                                                            sts_start_time: null,
                                                            sts_end_time: null,
                                                            ongoing_status: false,
                                                            vessel: VesselID,
                                                            sts_start_report: ReportID,
                                                            sts_end_report: null,
                                                            user_approved: false,
                                                            class_approved: false,
                                                            sts_operation: null,
                                                        });
                                                        setRefreshKey(refreshKey + 1);
                                                    }}
                                                >
                                                    Add another STS operation
                                                </Label>
                                            }
                                        </Col>
                                    </React.Fragment>
                                )
                            })}
                            {/* <Row>
                                    <CardBody>
                                        <Row className="justify-content-center">
                                            <Col xl={10}>
                                                <div className="timeline">
                                                    <div className="timeline-container">
                                                        <div className="timeline-end">
                                                            <p>Timeline</p>
                                                        </div>
                                                        <div className="timeline-continue">
                                                            <Row className="timeline-left">
                                                                <Col md={6} className="d-md-none d-block">
                                                                    <div className="timeline-icon">
                                                                        <i className="bx bx-user-pin text-primary h2 mb-0"></i>
                                                                    </div>
                                                                </Col>
                                                                <Col md={6}>
                                                                    <div className="timeline-box sts-timeline-box">
                                                                        <div className="event-content">
                                                                            <div className="timeline-text">
                                                                                <h3 className="font-size-18 mb-0">
                                                                                    Departure
                                                                                </h3>
                                                                                <h6>Port: Houston</h6>
                                                                                <p className="mb-0 mt-2 pt-1">
                                                                                    25 Jun 2023 18:00 UTC
                                                                                </p>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </Col>
                                                                <Col md={6} className="d-md-block d-none">
                                                                    <div className="timeline-icon">
                                                                        <i className="bx bx-briefcase-alt-2 text-primary h2 mb-0"></i>
                                                                    </div>
                                                                </Col>
                                                            </Row>
                                                            <Row className="timeline-right">
                                                                <Col md={6}>
                                                                    <div className="timeline-icon">
                                                                        <i className="bx bx-bar-chart-square text-primary h2 mb-0"></i>
                                                                    </div>
                                                                </Col>
                                                                <Col md={6}>
                                                                    <div className="timeline-box bg-light sts-timeline-box">
                                                                        <div className="timeline-date bg-danger text-center rounded">
                                                                            <h2><i className='mdi mdi-cancel icon_s28 text-white' /></h2>
                                                                        </div>
                                                                        <div className="event-content">
                                                                            <div className="timeline-text">
                                                                                <h3 className="font-size-18 text-secondary">
                                                                                    Loading operation start
                                                                                </h3>
                                                                                <p className="mb-0 mt-2 pt-1 text-secondary">
                                                                                    25 Jun 2023 02:00 UTC
                                                                                </p>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </Col>
                                                            </Row>

                                                            <Row className="timeline-left">
                                                                <Col md={6} className="d-md-none d-block">
                                                                    <div className="timeline-icon">
                                                                        <i className="bx bx-user-pin text-primary h2 mb-0"></i>
                                                                    </div>
                                                                </Col>
                                                                <Col md={6}>
                                                                    <div className="timeline-box sts-timeline-box">
                                                                        <div className="event-content">
                                                                            <div className="timeline-text">
                                                                                <h3 className="font-size-18 mb-0">
                                                                                    Arrival
                                                                                </h3>
                                                                                <h6>Port: Pajaritos</h6>
                                                                                <p className="mb-0 mt-2 pt-1">
                                                                                    29 Jun 2023 18:00 UTC
                                                                                </p>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </Col>
                                                                <Col md={6} className="d-md-block d-none">
                                                                    <div className="timeline-icon">
                                                                        <i className="bx bx-briefcase-alt-2 text-primary h2 mb-0"></i>
                                                                    </div>
                                                                </Col>
                                                            </Row>

                                                            <Row className="timeline-right">
                                                                <Col md={6}>
                                                                    <div className="timeline-icon">
                                                                        <i className="bx bx-pie-chart-alt text-primary h2 mb-0"></i>
                                                                    </div>
                                                                </Col>
                                                                <Col md={6}>
                                                                    <div className="timeline-box">
                                                                        <div className="timeline-date bg-primary text-center rounded">
                                                                            <h3 className="text-white mb-0">23</h3>
                                                                            <p className="mb-0 text-white-50">July</p>
                                                                        </div>
                                                                        <div className="event-content">
                                                                            <div className="timeline-text">
                                                                                <h3 className="font-size-18">
                                                                                    Timeline Event Five
                                                                                </h3>

                                                                                <p className="mb-0 mt-2 pt-1 text-muted">
                                                                                    Excepturi, obcaecati, quisquam id
                                                                                    molestias eaque asperiores voluptatibus
                                                                                    cupiditate error assumenda delectus odit
                                                                                    similique earum voluptatem Odit, itaque,
                                                                                    deserunt corporis vero ipsum nisi
                                                                                    repellat ...{" "}
                                                                                </p>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </Col>
                                                            </Row>

                                                            <Row className="timeline-left">
                                                                <Col md={6} className="d-md-none d-block">
                                                                    <div className="timeline-icon">
                                                                        <i className="bx bx-home-alt text-primary h2 mb-0"></i>
                                                                    </div>
                                                                </Col>
                                                                <Col md={6}>
                                                                    <div className="timeline-box">
                                                                        <div className="timeline-date bg-primary text-center rounded">
                                                                            <h3 className="text-white mb-0">25</h3>
                                                                            <p className="mb-0 text-white-50">June</p>
                                                                        </div>
                                                                        <div className="event-content">
                                                                            <div className="timeline-text">
                                                                                <h3 className="font-size-18">
                                                                                    Timeline Event End
                                                                                </h3>
                                                                                <p className="mb-0 mt-2 pt-1 text-muted">
                                                                                    Suspendisse tempor porttitor elit non
                                                                                    maximus. Sed suscipit, purus in
                                                                                    convallis condimentum, risus ex
                                                                                    pellentesque sapien, vel tempor arcu
                                                                                    dolor ut est. Nam ac felis id mauris
                                                                                    fermentum nisl pharetra auctor.
                                                                                </p>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </Col>
                                                                <Col md={6} className="d-md-block d-none">
                                                                    <div className="timeline-icon">
                                                                        <i className="bx bx-home-alt text-primary h2 mb-0"></i>
                                                                    </div>
                                                                </Col>
                                                            </Row>
                                                        </div>
                                                        <div className="timeline-start">
                                                            <p>End</p>
                                                        </div>
                                                        <div className="timeline-launch">
                                                            <div className="timeline-box">
                                                                <div className="timeline-text">
                                                                    <h3 className="font-size-18">
                                                                        Launched our company on 21 June 2021
                                                                    </h3>
                                                                    <p className="text-muted mb-0">
                                                                        Pellentesque sapien ut est.
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </Col>
                                        </Row>
                                    </CardBody>
                                </Row> */}
                            {deletePopoverBool && document.getElementById(`delete_sts_operation_${deleteIndex}`) !== null &&
                                <DeletePopOver
                                    state={deletePopoverBool}
                                    setState={setDeletePopoverBool}
                                    target={`delete_sts_operation_${deleteIndex}`}
                                    onClick={() => {
                                        filteredOperations?.splice(filteredOperations?.indexOf(deleteOperation?.sts_operation as number), 1);
                                        if (deleteOperation && deleteOperation?.id > 0) {
                                            apiGlobal.delete(`/sts_operation_detail/${deleteOperation?.id}/`, deleteOperation).then(async (res: any) => {
                                                if (res.status === 200 || res.status === 204) {
                                                    successToast("Data deleted successfully!");
                                                    STSOperationFormik?.values?.sts?.splice(deleteIndex, 1);
                                                    await queryClient.invalidateQueries(queryKeyes.vessel.STSOngoingReportingObject.key);
                                                    setRefreshKey(refreshKey + 1);
                                                    setDeletePopoverBool(false);
                                                    setDeleteIndex(null);
                                                    setDeleteOperation(null);
                                                }
                                            })
                                        } else {
                                            STSOperationFormik?.values?.sts?.splice(deleteIndex, 1);
                                            setDeletePopoverBool(false);
                                            setDeleteIndex(null);
                                            setDeleteOperation(null);
                                            setRefreshKey(refreshKey + 1);
                                        }

                                    }}
                                />
                            }
                        </CardBody>
                        {!editReportBool &&
                            <CardFooter className='p-2 py-3'>
                                <Row className="ele_row1">
                                    <div className="d-flex flex-wrap gap-5">
                                        <Button
                                            type="submit"
                                            color="primary"
                                            className="btn_size_cstm pos-end"
                                            disabled={STSOperationFormik?.isSubmitting}
                                        >
                                            {STSOperationFormik?.isSubmitting && <i className="ms-2 spinner-border spinner-border-sm text-light me-2" />}
                                            Next <i className="bx bx-chevron-right ms-1" />
                                        </Button>
                                        <Button
                                            type="button"
                                            color="primary"
                                            className="btn_size_cstm"
                                            onClick={() => {
                                                toggleTab(activeTab - 1);
                                                setErrorMessage(null)
                                            }}
                                        >
                                            <i className="bx bx-chevron-left me-1" /> Previous
                                        </Button>
                                    </div>
                                </Row>
                            </CardFooter>}
                        <FormValuesDebug values={[STSOperationFormik?.values, STSOperationFormik?.errors, STSOperationFormik?.touched]} />
                    </Form>
                }
            </Card>
        </React.Fragment>
    )
}

export default STSOperationComponent